import React from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { Field, ErrorMessage, useField, useFormikContext } from 'formik';
import { Select } from 'formik-material-ui';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';

// import utils
import { logger, mergeArray } from './../../../utils/utils';

import { useCategoryGetListByMode } from './../../../hooks/useData';

const SELECT_ALL_VALUE = '*';

/**
 * SelectPersonalDataCategory() (component)
 * @param {string} name, name of select element (default: category_id)
 * @param {string} id, id of select element (default: category_id)
 * @param {string} label, label of select element (default: "ประเภทของข้อมูลส่วนบุคคลที่มีการละเมิด")
 * @param {mode} mode reporter|staff, default: reporter
 * @param {bool} isSelectAll
 * @param {function} onChange, onChange
 * @param {bool} disabled, default: false
 * @param {object} className, className of select element
 * @param variant
 * @returns (element) SelectPersonalDataCategory
 */

/**
 * Modify: #1
 * Desc: Change props of Field
 *  - Change value argunment to user internal setFieldValue
 *  - Change FormHelperText(c) to Formik ErrorMessage(c) (https://formik.org/docs/api/errormessage)
 * Date: 20-05-2021
 */
const SelectPersonalDataCategory = (
  {
    name = 'category_id',
    id = 'category_id',
    label = 'ประเภทของข้อมูลส่วนบุคคลที่มีการละเมิด *',
    mode = 'reporter',
    isSelectAll = false,
    onChange,
    disabled = false,
    className,
    variant = 'standard',
  },
  ref
) => {
  logger('SelectPersonalDataCategory render');

  //Hook category by lang & mode
  let categories = useCategoryGetListByMode('TH', mode);

  // Hook Formik setFildValue
  const { setFieldValue } = useFormikContext();
  // https://formik.org/docs/api/useField (Test value from formik, instead state-value)
  const [field, meta] = useField(name);

  const getLevelData = (value) => {
    let ret = { data: {}, permissionLevels: [] };
    // Get permissionLevel in each case
    // Case 1: select all categories
    if (value === SELECT_ALL_VALUE) {
      let cats = categories.length ? categories : [];
      ret.data = cats;
      // Merge permissionLevel in categories list when cate more thna one (select all)
      ret.permissionLevels = mergeArray(
        categories.map((cate) =>
          cate.permissionLevel ? cate.permissionLevel : []
        )
      );
      // Case 2: select one category
    } else {
      let cat = categories.find((category) => category.id === value) || {};
      ret.data = cat;
      ret.permissionLevels = cat.permissionLevel || [];
    }
    return ret;
  };

  // Handle change select
  const handleChange = (e, c) => {
    setFieldValue(name, e.target.value);
    let context = typeof c.props.children === 'string' ? c.props.children : '';
    const { data, permissionLevels } = getLevelData(e.target.value);
    onChange && onChange(e, { data, permissionLevels }, context);
  };

  //formik.org/docs/examples/dependent-fields-async-api-request
  return (
    <React.Fragment>
      <FormControl
        fullWidth
        error={meta.error && meta.touched}
        className={className}
        variant={variant}
        size="small"
      >
        <InputLabel htmlFor={id} style={{ backgroundColor: 'white' }}>
          {label}
        </InputLabel>
        <Field
          component={Select}
          name={name}
          inputProps={{
            id,
            name,
          }}
          onChange={handleChange}
          disabled={disabled}
        >
          {!isSelectAll && (
            <MenuItem value="">
              <em>เลือก {label}</em>
            </MenuItem>
          )}
          {isSelectAll && (
            <MenuItem value={SELECT_ALL_VALUE}>* ทุกประเภท</MenuItem>
          )}
          {categories.map((category) => (
            <MenuItem value={category.id} key={category.id}>
              {category.name}
            </MenuItem>
          ))}
        </Field>

        <ErrorMessage name={name} component={FormHelperText}></ErrorMessage>
      </FormControl>
    </React.Fragment>
  );
};

// Types[, Rules] of props
SelectPersonalDataCategory.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  mode: PropTypes.string,
  onChange: PropTypes.func,
};

/**
 * By default it will only shallowly compare complex objects in the props object.
 * If you want control over the comparison,
 * you can also provide a custom comparison function as the second argument.
 * https://reactjs.org/docs/react-api.html
 * https://stackoverflow.com/questions/63502588/avoid-rerendering-child-when-passing-object-in-props
 */
export default React.memo(SelectPersonalDataCategory, isEqual);
